import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  X,
  Save,
  LogIn,
  XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { apiGet, apiPost, apiPatch, apiDelete, formatErrorMessage, ApiError } from '../../utils/catalogApi';

const AdminVendorSources = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    is_active: true,
    default_currency: 'USD',
    default_lead_time_days: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsAuthError(false);
      
      const data = await apiGet('/catalog-imports/admin/vendors', {
        q: search,
        is_active: showInactive ? undefined : 'true'
      });
      
      setVendors(data.vendors || []);
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        setIsAuthError(true);
        setError('Session expired. Please log in again.');
      } else {
        setError(formatErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [search, showInactive]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleOpenModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        code: vendor.code,
        is_active: vendor.is_active,
        default_currency: vendor.default_currency || 'USD',
        default_lead_time_days: vendor.default_lead_time_days || ''
      });
    } else {
      setEditingVendor(null);
      setFormData({
        name: '',
        code: '',
        is_active: true,
        default_currency: 'USD',
        default_lead_time_days: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const body = {
        name: formData.name,
        is_active: formData.is_active,
        default_currency: formData.default_currency,
        default_lead_time_days: formData.default_lead_time_days ? parseInt(formData.default_lead_time_days) : null
      };
      
      if (editingVendor) {
        await apiPatch(`/catalog-imports/admin/vendors/${editingVendor.id}`, body);
      } else {
        body.code = formData.code;
        await apiPost('/catalog-imports/admin/vendors', body);
      }
      
      handleCloseModal();
      fetchVendors();
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"? This cannot be undone.`)) return;
    
    try {
      await apiDelete(`/catalog-imports/admin/vendors/${vendor.id}`);
      fetchVendors();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleToggleActive = async (vendor) => {
    try {
      await apiPatch(`/catalog-imports/admin/vendors/${vendor.id}`, { is_active: !vendor.is_active });
      fetchVendors();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  if (loading && vendors.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-sources-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800">Vendor Sources</h2>
          <p className="text-sm text-ivory-500 font-body">Manage catalog vendors and their import settings</p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-vendor-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-ivory-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-ivory-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
        </div>
      </div>

      {error && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-3 ${
          isAuthError 
            ? 'bg-amber-50 border border-amber-200 text-amber-800' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {isAuthError ? (
            <>
              <LogIn className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Session Expired</p>
                <p className="text-sm opacity-80">Your login session has expired. Please log in again to continue.</p>
              </div>
              <Button 
                size="sm" 
                onClick={() => window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Log In
              </Button>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
              <Button variant="ghost" size="sm" onClick={fetchVendors}>
                Retry
              </Button>
            </>
          )}
        </div>
      )}

      {/* Vendors Table */}
      <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-ivory-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Currency</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Lead Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Stats</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-ivory-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ivory-100">
            {vendors.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-ivory-500">
                  No vendor sources found
                </td>
              </tr>
            ) : (
              vendors.map((vendor) => (
                <tr key={vendor.id} className="hover:bg-ivory-50" data-testid={`vendor-row-${vendor.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-ivory-800">{vendor.name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-ivory-600">{vendor.code}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-ivory-600">{vendor.default_currency}</td>
                  <td className="px-4 py-3 text-sm text-ivory-600">
                    {vendor.default_lead_time_days ? `${vendor.default_lead_time_days} days` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-ivory-500">
                    <span className="mr-2">{vendor.mapping_profiles_count || 0} profiles</span>
                    <span>{vendor.imports_count || 0} imports</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(vendor)}
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        vendor.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {vendor.is_active ? (
                        <><ToggleRight className="w-3 h-3" /> Active</>
                      ) : (
                        <><ToggleLeft className="w-3 h-3" /> Inactive</>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenModal(vendor)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(vendor)}
                        className="text-red-600 hover:text-red-700"
                        disabled={vendor.imports_count > 0}
                        title={vendor.imports_count > 0 ? 'Cannot delete vendor with imports' : 'Delete vendor'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-ivory-200 flex items-center justify-between">
              <h3 className="font-semibold text-ivory-800">
                {editingVendor ? 'Edit Vendor Source' : 'Add Vendor Source'}
              </h3>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., SanMar"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-1">Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  placeholder="e.g., sanmar"
                  disabled={!!editingVendor}
                  className={editingVendor ? 'bg-ivory-50' : ''}
                />
                {editingVendor && (
                  <p className="text-xs text-ivory-500 mt-1">Code cannot be changed after creation</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-1">Currency</label>
                  <select
                    className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                    value={formData.default_currency}
                    onChange={(e) => setFormData({ ...formData, default_currency: e.target.value })}
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-1">Lead Time (days)</label>
                  <Input
                    type="number"
                    value={formData.default_lead_time_days}
                    onChange={(e) => setFormData({ ...formData, default_lead_time_days: e.target.value })}
                    placeholder="e.g., 5"
                    min="0"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is-active" className="text-sm text-ivory-700">Active</label>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-ivory-200 flex justify-end gap-3">
              <Button variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !formData.name || !formData.code}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendorSources;
